import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { ColorRing } from 'react-loader-spinner'

// Reusing original styles
import '../Make/Make.css'

import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import renderImage from '../../source/importImg'
import getBackendImage from '../../source/imageUtil'
import { uploadImage } from '../../source/imageUtil'
import { getKitsForDevice } from '../../utils/expansionKits'

const BACKEND_URL = process.env.REACT_APP_API_BASE

const MakeAdmin = (props) => {
    const history = useHistory()
    const location = useLocation()
    const device = sessionStorage.getItem('allowedDevice') || 'Toki'
    const deviceVersion = sessionStorage.getItem('deviceVersion') || '1.0.0'

    const SCREEN_NAME_MAP = {
        playdynamex: 'Mechanical',
        assembly: 'Electronic',
        hex: 'Coding',
        simulate: 'Simulate',
        play: 'Play',
        integrate: 'Integrate',
    }

    const [program, setProgram] = useState(null)
    const [loading, setLoading] = useState(true)

    // --- NEW: TTS Config State ---
    const [ttsApiKey, setTtsApiKey] = useState(
        localStorage.getItem('freepikApiKey') || ''
    )
    const [ttsVoiceId, setTtsVoiceId] = useState(
        localStorage.getItem('elevenVoiceId') || ''
    )

    // Github OAuth state
    const [user, setUser] = useState(null)
    const [authLoading, setAuthLoading] = useState(true)
    // NEW: Login UI states
    const [loginError, setLoginError] = useState('')
    const [isLoggingIn, setIsLoggingIn] = useState(false)

    useEffect(() => {
        // 1. Check for errors returned from the backend redirect
        const params = new URLSearchParams(location.search)
        const errorParam = params.get('error')

        if (errorParam) {
            if (errorParam === 'unauthorized') {
                setLoginError(
                    'Login authentication failed: Account not authorized.'
                )
            } else {
                setLoginError('Login authentication failed. Please try again.')
            }
            // Clean the URL so the error doesn't persist on manual refresh
            history.replace('/makeAdmin')
        }

        // 2. Fetch User session
        fetch(`${BACKEND_URL}/me`, {
            credentials: 'include',
        })
            .then((res) => res.json())
            .then((data) => {
                setUser(data)
                setAuthLoading(false)
            })
            .catch(() => {
                setUser(null)
                setAuthLoading(false)
            })
    }, [location.search, history])

    useEffect(() => {
        if (user) {
            fetchProgram()
        }
    }, [user])

    const fetchProgram = () => {
        setLoading(true)
        fetch(`${BACKEND_URL}/api/program/staging/${device.toLowerCase()}`, {
            credentials: 'include',
        })
            .then(async (res) => {
                if (!res.ok) {
                    // No staging program for this device yet (e.g. 404 in prod).
                    // Fall back to an empty program so the admin can create one
                    // instead of white-screening.
                    console.warn(
                        `Staging program for "${device}" returned ${res.status}; starting with an empty program.`
                    )
                    return { subprograms: [] }
                }
                return res.json()
            })
            .then((data) => {
                // Guard against a malformed payload missing the subprograms array
                setProgram(
                    data && Array.isArray(data.subprograms)
                        ? data
                        : { ...(data || {}), subprograms: [] }
                )
                setLoading(false)
            })
            .catch((err) => {
                console.error('Failed to load staging program:', err)
                setProgram({ subprograms: [] })
                setLoading(false)
            })
    }

    const saveProgram = async (updatedProgram) => {
        try {
            setLoading(true)
            await fetch(
                `${BACKEND_URL}/api/program/staging/${device.toLowerCase()}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updatedProgram),
                    credentials: 'include',
                }
            )
            setProgram(updatedProgram)
        } catch (err) {
            console.error('Failed to save program:', err)
            alert('Failed to save changes.')
        } finally {
            setLoading(false)
        }
    }

    // --- NEW: Handle saving TTS settings ---
    const handleSaveTtsConfig = () => {
        localStorage.setItem('freepikApiKey', ttsApiKey.trim())
        localStorage.setItem('elevenVoiceId', ttsVoiceId.trim())
        alert('TTS Settings saved locally to your browser!')
    }

    const handleDownloadStaging = async () => {
        try {
            const res = await fetch(
                `${BACKEND_URL}/api/admin/download-staging`,
                {
                    credentials: 'include',
                }
            )
            if (!res.ok) {
                alert('Download failed: ' + res.status)
                return
            }
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'stagingData.zip'
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
        } catch (err) {
            console.error('Download error:', err)
            alert('Download failed')
        }
    }

    // --- Publish popup state ---
    // const [showPublishPopup, setShowPublishPopup] = useState(false)
    // const [publishStep, setPublishStep] = useState('subprogram') // 'subprogram' | 'playscreen'
    // const [selectedSubId, setSelectedSubId] = useState(null)
    // const [selectedPlayScreenId, setSelectedPlayScreenId] = useState(null)

    // --- Reorder subprograms ---
    const moveSubprogram = (index, direction) => {
        const subs = [...program.subprograms]
        const newIndex = index + direction
        if (newIndex < 0 || newIndex >= subs.length) return
        ;[subs[index], subs[newIndex]] = [subs[newIndex], subs[index]]
        saveProgram({ ...program, subprograms: subs })
    }

    const handlePublish = async () => {
        // Step 1: Check missing images
        const missingImages = program?.subprograms?.filter(
            (s) => !s.image || s.image === 'projectDefault'
        )
        if (missingImages && missingImages.length > 0) {
            const names = missingImages.map((s) => s.title).join(', ')
            window.alert(
                `Cannot publish! The following subprograms are missing a custom image: ${names}.\n\nPlease set an image for each by clicking "Change Image".`
            )
            return
        }

        setLoading(true)

        // Step 2: Check audio files for all screens
        try {
            const audioRes = await fetch(`${BACKEND_URL}/api/check-audio`, {
                credentials: 'include',
            })
            const audioData = await audioRes.json()

            if (!audioData.ok) {
                const lines = audioData.missing.map(
                    (m) =>
                        `• ${m.subprogram} → ${SCREEN_NAME_MAP[m.screenType]} (ID: ${m.screenId})`
                )
                window.alert(
                    `Cannot publish! The following screens are missing audio files:\n\n${lines.join('\n')}\n\nPlease generate voiceovers for these screens before publishing.`
                )
                setLoading(false)
                return
            }
        } catch (err) {
            console.error('Audio check failed:', err)
            alert('Failed to check audio files. Please try again.')
            setLoading(false)
            return
        }

        // Step 3: Final Confirmation & Publish
        const confirmPublish = window.confirm(
            'Are you sure you want to upload all the data? This is permanent and will overwrite live data for regular users.'
        )
        if (!confirmPublish) {
            setLoading(false)
            return
        }

        try {
            const response = await fetch(`${BACKEND_URL}/api/publish-staging`, {
                method: 'POST',
                credentials: 'include',
            })

            if (response.ok) {
                alert('Success! All staging data is now live.')
            } else {
                alert('Error: Failed to publish data.')
            }
        } catch (err) {
            console.error('Publish failed:', err)
            alert('A network error occurred while publishing.')
        } finally {
            setLoading(false)
        }
    }

    // const handleSubprogramSelected = (subId) => {
    //     const sub = program.subprograms.find((s) => s.id === subId)
    //     const playScreens = (sub?.screens || []).filter(
    //         (s) => s.type === 'play'
    //     )

    //     if (playScreens.length === 0) {
    //         window.alert(
    //             `The subprogram "${sub.title}" has no Play screen. Please choose a subprogram that has at least one Play screen.`
    //         )
    //         return
    //     }

    //     setSelectedSubId(subId)
    //     setSelectedPlayScreenId(null)
    //     setPublishStep('playscreen')
    // }

    const goBack = () => history.push('/admin')
    const openSubprogram = (subId) => history.push(`/makeAdmin/${subId}`)

    const handleDelete = async (e, subId) => {
        e.stopPropagation()
        if (
            !window.confirm(
                'Are you sure you want to delete this subprogram? This will also delete all associated screen files.'
            )
        )
            return

        try {
            await fetch(`${BACKEND_URL}/api/project/${subId}`, {
                method: 'DELETE',
                credentials: 'include',
            })
        } catch (err) {
            console.error('Failed to delete subprogram files:', err)
        }

        const updatedSubprograms = program.subprograms.filter(
            (s) => s.id !== subId
        )
        saveProgram({ ...program, subprograms: updatedSubprograms })
    }

    const handleEditTitle = (e, subId) => {
        e.stopPropagation()

        const sub = program.subprograms.find((s) => s.id === subId)

        const newTitle = window.prompt(
            'Enter new title for subprogram:',
            sub.title
        )

        const MAX_TITLE_LENGTH = 42

        if (
            newTitle &&
            newTitle.trim() !== '' &&
            newTitle.trim() !== sub.title
        ) {
            if (newTitle.trim().length > MAX_TITLE_LENGTH) {
                alert(`Title must be ${MAX_TITLE_LENGTH} characters or less.`)
                return
            }

            const updatedSubprograms = program.subprograms.map((s) =>
                s.id === subId ? { ...s, title: newTitle.trim() } : s
            )

            saveProgram({
                ...program,
                subprograms: updatedSubprograms,
            })
        }
    }

    const handleCreate = async () => {
        const title = window.prompt('Enter title for the new subprogram:')
        if (title && title.trim() !== '') {
            // --- NEW: Create a URL-friendly slug, but attach a unique random string ---
            const safeSlug = title
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
            const randomString = Math.random().toString(36).substring(2, 7) // Generates something like 'a8f4z'
            const newId = `${safeSlug}-${randomString}`

            try {
                await fetch(`${BACKEND_URL}/api/project/${newId}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ title: title.trim() }),
                })
            } catch (err) {
                console.error(
                    'Failed to generate project backend resources:',
                    err
                )
                alert('Could not register project on backend.')
                return
            }

            const newSub = {
                id: newId,
                title: title.trim(),
                description: 'New description goes here.',
                image: 'projectDefault',
                screens: [],
            }
            saveProgram({
                ...program,
                subprograms: [...program.subprograms, newSub],
            })
        }
    }

    // Expansion kits this device supports — options for each subprogram's dropdown
    const kitOptions = getKitsForDevice(device)

    const handleKitChange = (e, subId) => {
        e.stopPropagation()
        const kit = e.target.value // '' means "no kit"
        const updatedSubprograms = program.subprograms.map((s) =>
            s.id === subId ? { ...s, kit } : s
        )
        saveProgram({ ...program, subprograms: updatedSubprograms })
    }

    const handleImageUpload = async (e, subId) => {
        e.stopPropagation()
        const file = e.target.files[0]
        if (!file) return

        // PNG Validation
        if (
            file.type !== 'image/png' &&
            !file.name.toLowerCase().endsWith('.png')
        ) {
            alert('Please select a valid .png image.')
            e.target.value = ''
            return
        }

        if (file.size > 10 * 1024 * 1024) {
            alert('Image is too large! Maximum allowed size is 10MB.')
            e.target.value = ''
            return
        }

        try {
            const filename = await uploadImage(file)
            const confirm = window.confirm(
                'Are you sure you want to update the image for this subprogram?'
            )
            if (confirm !== true) return

            const updatedSubprograms = program.subprograms.map((s) =>
                s.id === subId ? { ...s, image: filename } : s
            )
            saveProgram({ ...program, subprograms: updatedSubprograms })
        } catch (err) {
            console.error('Upload failed', err)
            alert('Image upload failed')
        }
    }

    // NEW: Handle login button click
    const handleLoginClick = () => {
        setIsLoggingIn(true)
        setLoginError('')
        window.location.href = `${BACKEND_URL}/auth/github?state=/admin`
    }

    if (authLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <ColorRing
                    visible={true}
                    height="80"
                    width="80"
                    colors={[
                        '#e15b64',
                        '#f47e60',
                        '#f8b26a',
                        '#abbd81',
                        '#849b87',
                    ]}
                />
            </div>
        )
    }

    if (!user) {
        return (
            <div className="Main_Select">
                <div className="Select_Headers">
                    <MakeHeader
                        title="Admin Access"
                        titlePosition="left"
                        isGoBack={true}
                        goBack={() => history.push('/admin')}
                        showAccountSetting={false}
                        showBluetoothBtn={false}
                    />
                </div>

                <div
                    className="TokiKits_Body"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '60vh',
                    }}
                >
                    <div
                        style={{
                            textAlign: 'center',
                            maxWidth: '380px',
                            width: '100%',
                            backgroundColor: 'white',
                            padding: '40px 30px',
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                            border: '1px solid #eaeaea',
                        }}
                    >
                        <div style={{ marginBottom: '20px' }}>
                            <svg
                                height="48"
                                viewBox="0 0 16 16"
                                version="1.1"
                                width="48"
                                aria-hidden="true"
                            >
                                <path
                                    fill="#2f2e65"
                                    fillRule="evenodd"
                                    d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"
                                ></path>
                            </svg>
                        </div>

                        <div
                            style={{
                                fontSize: '20px',
                                color: '#2f2e65',
                                marginBottom: '10px',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                            }}
                        >
                            Toki Admin Portal
                        </div>

                        <div
                            style={{
                                fontSize: '14px',
                                color: '#666',
                                marginBottom: '20px',
                                lineHeight: '1.5',
                            }}
                        >
                            Sign in with an authorized GitHub account to manage
                            course content and publish updates.
                        </div>

                        {/* Error Message Display */}
                        {loginError && (
                            <div
                                style={{
                                    marginBottom: '20px',
                                    padding: '12px',
                                    backgroundColor: '#fff2f2',
                                    color: '#d32f2f',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    border: '1px solid #ffcdd2',
                                    textAlign: 'center',
                                    fontWeight: '500',
                                }}
                            >
                                {loginError}
                            </div>
                        )}

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                            }}
                        >
                            <button
                                onClick={handleLoginClick}
                                disabled={isLoggingIn}
                                style={{
                                    width: '100%',
                                    padding: '12px 18px',
                                    backgroundColor: isLoggingIn
                                        ? '#5c5b8b'
                                        : '#2f2e65',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: 'bold',
                                    cursor: isLoggingIn
                                        ? 'not-allowed'
                                        : 'pointer',
                                    transition: 'background-color 0.2s',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '10px',
                                }}
                            >
                                {isLoggingIn
                                    ? 'Connecting to GitHub...'
                                    : loginError
                                      ? 'Try GitHub Login Again'
                                      : 'Login with GitHub'}
                            </button>

                            {/* NEW: Escape hatch button so they don't get stuck */}
                            <button
                                onClick={() => history.push('/kitSelection')}
                                disabled={isLoggingIn}
                                style={{
                                    width: '100%',
                                    padding: '12px 18px',
                                    backgroundColor: 'transparent',
                                    color: '#666',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    cursor: isLoggingIn
                                        ? 'not-allowed'
                                        : 'pointer',
                                    transition: 'background-color 0.2s',
                                }}
                                onMouseOver={(e) =>
                                    !isLoggingIn &&
                                    (e.target.style.backgroundColor = '#f5f5f5')
                                }
                                onMouseOut={(e) =>
                                    !isLoggingIn &&
                                    (e.target.style.backgroundColor =
                                        'transparent')
                                }
                            >
                                Return to Toki Engine
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="Main_Select">
            <div className="Select_Headers">
                <MakeHeader
                    title={`Admin: ${device} Courses`}
                    titlePosition="left"
                    isGoBack={true}
                    goBack={goBack}
                    showAccountSetting={true}
                    showBluetoothBtn={true}
                />

                <button
                    onClick={handlePublish}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: '300px',
                        transform: 'translateY(-50%)',
                        backgroundColor: '#ff4b4b',
                        color: 'white',
                        border: 'none',
                        padding: '8px 20px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        zIndex: 1000002,
                        boxShadow: '0 4px 10px rgba(255, 75, 75, 0.3)',
                        transition: 'background-color 0.2s',
                    }}
                    onMouseOver={(e) =>
                        (e.target.style.backgroundColor = '#e03e3e')
                    }
                    onMouseOut={(e) =>
                        (e.target.style.backgroundColor = '#ff4b4b')
                    }
                >
                    Publish to Live
                </button>

                <button
                    onClick={() => history.push('/kitSelection')}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: '180px',
                        transform: 'translateY(-50%)',
                        padding: '6px 12px',
                        backgroundColor: '#2f2e65',
                        color: 'white',
                        border: 'none',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        zIndex: 1000002,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    }}
                >
                    Exit Admin
                </button>
            </div>

            {/* --- NEW: TTS Admin Settings Panel --- */}
            <div
                style={{
                    margin: '10px auto 0 auto', // Centered margins, drops down naturally below header
                    maxWidth: '700px',
                    width: 'calc(100% - 8rem)', // Matches the structural left/right padding of .TokiKits_Body
                    padding: '10px 15px',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff', // Ensures readability over layout shifts
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    boxSizing: 'border-box',
                }}
            >
                <strong style={{ color: '#2f2e65', fontSize: '14px' }}>
                    Voiceover Settings:
                </strong>

                <input
                    type="text"
                    placeholder="ElevenLabs API Key"
                    value={ttsApiKey}
                    onChange={(e) => setTtsApiKey(e.target.value)}
                    style={{
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        flex: 1,
                        fontSize: '13px',
                    }}
                />

                <input
                    type="text"
                    placeholder="Voice ID"
                    value={ttsVoiceId}
                    onChange={(e) => setTtsVoiceId(e.target.value)}
                    style={{
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        // width: '250px',
                        fontSize: '13px',
                    }}
                />

                <button
                    onClick={handleSaveTtsConfig}
                    // disabled={savingTts}
                    style={{
                        backgroundColor: '#ff9800',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                    }}
                >
                    Save
                </button>
            </div>

            <div className="TokiKits_Body">
                {loading && (
                    <div
                        style={{
                            gridColumn: '1 / -1',
                            textAlign: 'center',
                            padding: '40px',
                        }}
                    >
                        <ColorRing
                            visible={true}
                            height="60"
                            width="60"
                            colors={[
                                '#e15b64',
                                '#f47e60',
                                '#f8b26a',
                                '#abbd81',
                                '#849b87',
                            ]}
                        />
                    </div>
                )}

                {!loading &&
                    program &&
                    (program.subprograms || []).map((sub) => (
                        <div
                            key={sub.id}
                            className="TokiKit_Card"
                            onClick={() => openSubprogram(sub.id)}
                            style={{ paddingBottom: '10px' }} // extra padding for admin buttons
                        >
                            <div
                                className="TokiKit_CardImg"
                                style={{ position: 'relative' }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        zIndex: 5,
                                    }}
                                >
                                    <label
                                        style={{
                                            background: '#007BFF',
                                            color: 'white',
                                            padding: '6px 10px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            fontFamily:
                                                'Halcyon_SemiBold, sans-serif',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Change Image
                                        <input
                                            type="file"
                                            accept=".png, image/png"
                                            style={{ display: 'none' }}
                                            onChange={(e) =>
                                                handleImageUpload(e, sub.id)
                                            }
                                        />
                                    </label>
                                </div>
                                <img
                                    src={getBackendImage(
                                        sub.image || 'projectDefault'
                                    )}
                                    alt={sub.title}
                                />
                            </div>

                            <p className="TokiKit_CardName">{sub.title}</p>

                            {/* Expansion kit tag — which kit this subprogram belongs to */}
                            <select
                                value={sub.kit || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleKitChange(e, sub.id)}
                                style={{
                                    margin: '4px 0 8px',
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #ccc',
                                    fontSize: '12px',
                                    maxWidth: '90%',
                                    cursor: 'pointer',
                                    fontFamily: 'Halcyon_SemiBold, sans-serif',
                                }}
                            >
                                <option value="">No expansion kit</option>
                                {kitOptions.map((kit) => (
                                    <option key={kit.id} value={kit.id}>
                                        {kit.name}
                                    </option>
                                ))}
                            </select>

                            {/* Admin Controls embedded at the bottom of the card */}
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px',
                                    width: '100%',
                                    marginTop: 'auto', // Pushes controls to the bottom if names vary in length
                                }}
                            >
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        moveSubprogram(
                                            program.subprograms.indexOf(sub),
                                            -1
                                        )
                                    }}
                                    disabled={
                                        program.subprograms.indexOf(sub) === 0
                                    }
                                    title="Move left"
                                    style={{
                                        background: 'none',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        cursor:
                                            program.subprograms.indexOf(sub) ===
                                            0
                                                ? 'not-allowed'
                                                : 'pointer',
                                        padding: '2px 6px',
                                        color:
                                            program.subprograms.indexOf(sub) ===
                                            0
                                                ? '#ccc'
                                                : '#555',
                                    }}
                                >
                                    ◀
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        moveSubprogram(
                                            program.subprograms.indexOf(sub),
                                            1
                                        )
                                    }}
                                    disabled={
                                        program.subprograms.indexOf(sub) ===
                                        program.subprograms.length - 1
                                    }
                                    title="Move right"
                                    style={{
                                        background: 'none',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        cursor:
                                            program.subprograms.indexOf(sub) ===
                                            program.subprograms.length - 1
                                                ? 'not-allowed'
                                                : 'pointer',
                                        padding: '2px 6px',
                                        color:
                                            program.subprograms.indexOf(sub) ===
                                            program.subprograms.length - 1
                                                ? '#ccc'
                                                : '#555',
                                    }}
                                >
                                    ▶
                                </button>

                                <span
                                    style={{ color: '#ccc', margin: '0 2px' }}
                                >
                                    |
                                </span>

                                <button
                                    onClick={(e) => handleEditTitle(e, sub.id)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#007BFF',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    Edit
                                </button>

                                <span
                                    style={{ color: '#ccc', margin: '0 2px' }}
                                >
                                    |
                                </span>

                                <button
                                    onClick={(e) => handleDelete(e, sub.id)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#DC3545',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}

                {!loading && program && (
                    <div
                        className="TokiKit_Card"
                        onClick={handleCreate}
                        style={{
                            background: 'rgba(255,255,255,0.5)',
                            border: '2px dashed #999',
                            boxShadow: 'none',
                            justifyContent: 'center',
                            minHeight: '34vh', // Matches roughly the height of the image + text combined
                            color: '#666',
                            fontWeight: 'bold',
                        }}
                    >
                        + Create new subprogram
                    </div>
                )}
            </div>

            {/* Download stagingData button (bottom-right corner) */}
            <button
                className="StagingDownload_Btn"
                onClick={handleDownloadStaging}
                title="Download stagingData"
            >
                <svg
                    viewBox="0 0 24 24"
                    width="48"
                    height="48"
                    fill="currentColor"
                >
                    <path d="M12 16l-5-5h3V4h4v7h3l-5 5zm-7 4v-2h14v2H5z" />
                </svg>
            </button>

            {/* --- Publish Popup (Subprogram + Play Screen selection) --- */}
            {/* {showPublishPopup && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={() => setShowPublishPopup(false)}
                >
                    <div
                        style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '30px',
                            minWidth: '400px',
                            maxWidth: '520px',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {publishStep === 'subprogram' && (
                            <>
                                <h3
                                    style={{
                                        margin: '0 0 8px',
                                        color: '#2f2e65',
                                        fontSize: '18px',
                                    }}
                                >
                                    Select Capstone Project
                                </h3>
                                <p
                                    style={{
                                        color: '#666',
                                        fontSize: '14px',
                                        margin: '0 0 16px',
                                    }}
                                >
                                    Choose which project the "Play" button should open:
                                </p>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                    }}
                                >
                                    {program.subprograms.map((sub) => (
                                        <div
                                            key={sub.id}
                                            onClick={() =>
                                                handleSubprogramSelected(sub.id)
                                            }
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '10px 14px',
                                                borderRadius: '10px',
                                                border: '1px solid #e0e0e0',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                backgroundColor: '#fafafa',
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    '#eef0ff'
                                                e.currentTarget.style.borderColor =
                                                    '#2f2e65'
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    '#fafafa'
                                                e.currentTarget.style.borderColor =
                                                    '#e0e0e0'
                                            }}
                                        >
                                            <img
                                                src={getBackendImage(
                                                    sub.image ||
                                                        'projectDefault'
                                                )}
                                                alt={sub.title}
                                                style={{
                                                    width: '44px',
                                                    height: '44px',
                                                    borderRadius: '8px',
                                                    objectFit: 'cover',
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <span
                                                style={{
                                                    fontSize: '15px',
                                                    fontWeight: '600',
                                                    color: '#2f2e65',
                                                }}
                                            >
                                                {sub.title}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {publishStep === 'playscreen' &&
                            (() => {
                                const selectedSub = program.subprograms.find(
                                    (s) => s.id === selectedSubId
                                )
                                const playScreens = (
                                    selectedSub?.screens || []
                                ).filter((s) => s.type === 'play')

                                return (
                                    <>
                                        <h3
                                            style={{
                                                margin: '0 0 8px',
                                                color: '#2f2e65',
                                                fontSize: '18px',
                                            }}
                                        >
                                            Choose Play Screen
                                        </h3>
                                        <p
                                            style={{
                                                color: '#666',
                                                fontSize: '14px',
                                                margin: '0 0 6px',
                                            }}
                                        >
                                            Subprogram:{' '}
                                            <strong>
                                                {selectedSub?.title}
                                            </strong>
                                        </p>
                                        <p
                                            style={{
                                                color: '#888',
                                                fontSize: '13px',
                                                margin: '0 0 16px',
                                            }}
                                        >
                                            Choose which Play screen the "Play" button should open:
                                        </p>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '8px',
                                                marginBottom: '20px',
                                            }}
                                        >
                                            {playScreens.map((scr) => (
                                                <label
                                                    key={scr.id}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        padding: '10px 14px',
                                                        borderRadius: '10px',
                                                        border:
                                                            selectedPlayScreenId ===
                                                            scr.id
                                                                ? '2px solid #2f2e65'
                                                                : '1px solid #e0e0e0',
                                                        cursor: 'pointer',
                                                        backgroundColor:
                                                            selectedPlayScreenId ===
                                                            scr.id
                                                                ? '#eef0ff'
                                                                : '#fafafa',
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="playScreenChoice"
                                                        checked={
                                                            selectedPlayScreenId ===
                                                            scr.id
                                                        }
                                                        onChange={() =>
                                                            setSelectedPlayScreenId(
                                                                scr.id
                                                            )
                                                        }
                                                        style={{
                                                            accentColor:
                                                                '#2f2e65',
                                                        }}
                                                    />
                                                    <span
                                                        style={{
                                                            fontSize: '15px',
                                                            fontWeight: '600',
                                                            color: '#2f2e65',
                                                        }}
                                                    >
                                                        {scr.id}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>

                                        <button
                                            onClick={handleConfirmPublish}
                                            disabled={!selectedPlayScreenId}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                backgroundColor:
                                                    selectedPlayScreenId
                                                        ? '#ff4b4b'
                                                        : '#ccc',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '15px',
                                                fontWeight: 'bold',
                                                cursor: selectedPlayScreenId
                                                    ? 'pointer'
                                                    : 'not-allowed',
                                                marginBottom: '8px',
                                                transition:
                                                    'background-color 0.2s',
                                            }}
                                        >
                                            Publish to Live
                                        </button>

                                        <button
                                            onClick={() =>
                                                setPublishStep('subprogram')
                                            }
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                background: 'transparent',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                color: '#666',
                                            }}
                                        >
                                            ← Back to subprograms
                                        </button>
                                    </>
                                )
                            })()}

                        <button
                            onClick={() => setShowPublishPopup(false)}
                            style={{
                                marginTop: '12px',
                                padding: '8px 16px',
                                background: 'transparent',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: '#666',
                                width: '100%',
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )} */}
        </div>
    )
}

const mapStateToProps = (state) => ({
    languageName: state.device.language,
})

export default connect(mapStateToProps)(MakeAdmin)
